import React, { useState, useEffect } from 'react';
import type { UploadChangeParam } from 'antd/es/upload';
import { message, Spin, Upload, Button } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { uploadDocument } from 'api/documentApi';
import { gpUploadDocument } from 'api/group';
import { getProviderPrivateFileSigned } from 'api/common';
import UploadIcon from 'components/form/FileUploadPrivate/upload.svg';
import s from './s.module.less';
import clsx from 'classnames';

const { Dragger } = Upload;

interface DocumentUploadProps {
    clientId?: number;
    value?: string;
    tips?: string;
    accept?: string;
    onChange?: (fileUploadId: string) => void;
    onChangeFullUrl?: (fullUrl: string) => void;
    title?: string | React.ReactNode;
    previewMode?: 'horizontal' | 'vertical';
    isGroupPractice?: boolean;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
    clientId,
    accept,
    value,
    tips,
    onChange,
    onChangeFullUrl,
    title,
    previewMode = 'horizontal',
    isGroupPractice,
}) => {
    const [loading, setLoading] = useState(false);
    const fileInfoRef = React.useRef<UploadChangeParam<UploadFile>>();
    const [showUploadList, setShowUploadList] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
    const [previewFileName, setPreviewFileName] = useState<string>();

    const handleChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        fileInfoRef.current = info;
        if (info.file.status === 'uploading') {
            setLoading(true);
        }
    };

    const getPreview = async (v?: string) => {
        if (v) {
            try {
                const res = await getProviderPrivateFileSigned(v);
                if (!res.error && res.data?.data?.singedUrl) {
                    setPreviewUrl(res.data.data.singedUrl);
                    return res.data.data.singedUrl;
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        onChange: handleChange,
        accept: accept || 'image/*,application/pdf',
        showUploadList,
        onDrop() {
            // Handle drop event if needed
        },
        customRequest: async (data) => {
            if (!clientId) {
                message.error('Please select a client');
                return;
            }

            try {
                const res = isGroupPractice
                    ? await gpUploadDocument({
                        file: data.file as File,
                        identityId: clientId,
                    })
                    : await uploadDocument({
                        file: data.file as File,
                        clientId: clientId.toString(),
                    });

                if (!res.error) {
                    if (fileInfoRef.current) {
                        fileInfoRef.current.file.status = 'done';
                    }
                    setShowUploadList(true);

                    const fileUploadId = res.data?.data?.id;
                    const fileUri = res.data?.data?.fileUri;
                    const fileName = res.data?.data?.fileOriginName;
                    setPreviewFileName(fileName);

                    // 调用 onChange 更新 Form 的值
                    if (onChange && fileUploadId) {
                        onChange(fileUploadId);
                    }

                    const fullUrl = await getPreview(fileUri);
                    onChangeFullUrl?.(fullUrl || '');
                } else {
                    message.error(res.error || 'Upload failed. Please try again.');
                    if (fileInfoRef.current) {
                        fileInfoRef.current.file.status = 'error';
                    }
                }
            } catch (e) {
                setShowUploadList(false);
                message.error('Upload failed');
                console.error(e);
                if (fileInfoRef.current) {
                    fileInfoRef.current.file.status = 'error';
                }
            } finally {
                setLoading(false);
            }
        },
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                {!value && (
                    <Dragger {...props}>
                        {title && <p className={s.title}>{title}</p>}
                        <p className={s.iconWrap}>
                            <img src={UploadIcon} className={s.icon} alt="Upload" />
                        </p>
                        <p className={s.uploadText}>
                            <span>Upload a file</span> or drag and drop
                        </p>
                        <p className={s.uploadHint}>
                            {tips ?? 'PNG, JPG, PDF up to 10MB'}
                        </p>
                    </Dragger>
                )}
            </Spin>
            {previewFileName && (
                previewFileName.endsWith('.pdf') ? (
                    <div className={clsx(s.preview, s[previewMode])}>
                        <div className={s.fileInfo}>
                            <div className={s.fileName}>
                                {previewFileName}
                            </div>
                            <div className={s.fileType}>
                                {previewFileName?.split('.').pop()?.toUpperCase()}
                            </div>
                        </div>
                        <Upload {...props} showUploadList={false}>
                            <Button className={s.changeBtn}>Change</Button>
                        </Upload>
                    </div>
                ) : (
                    <div className={clsx(s.preview, s[previewMode])}>
                        <div className={s.previewImgWrap}>
                            <img src={previewUrl} alt="preview" className={s.previewImg} />
                        </div>
                        <Upload {...props} showUploadList={false}>
                            <Button className={s.changeBtn}>Change</Button>
                        </Upload>
                    </div>
                )
            )}
        </div >
    );
};

export default DocumentUpload;
